import { useState } from 'react';
import { Icon, tokens } from '@/design';
import { useViewerStore, type SavedView } from '@/state/useViewerStore';

export interface SavedViewsPanelProps {
  className?: string;
}

/**
 * SavedViewsPanel — GVW-048.
 *
 * Token-only left-rail list for saved views. Uses the existing viewer store
 * actions introduced before WP-E11.
 */
export function SavedViewsPanel({ className }: SavedViewsPanelProps) {
  const savedViewsFromHook = useViewerStore((state) => state.savedViews);
  const savedViews =
    savedViewsFromHook.length > 0 ? savedViewsFromHook : useViewerStore.getState().savedViews;
  const saveCurrentView = useViewerStore.getState().saveCurrentView;
  const restoreSavedView = useViewerStore.getState().restoreSavedView;
  const removeSavedView = useViewerStore.getState().removeSavedView;
  const [viewName, setViewName] = useState('');
  const [notice, setNotice] = useState('');

  const handleSaveView = () => {
    const name = viewName.trim();
    if (!name) return;
    saveCurrentView(name);
    setViewName('');
    setNotice('View saved successfully.');
  };

  const handleApplyView = (view: SavedView) => {
    restoreSavedView(view.id);
    setNotice(`Applied ${view.name}.`);
  };

  const handleDelete = (view: SavedView) => {
    removeSavedView(view.id);
    setNotice(`Deleted ${view.name}.`);
  };

  return (
    <section
      className={className}
      role="group"
      aria-labelledby="saved-views-heading"
      data-gvw-id="GVW-048"
      style={{
        padding: tokens.space[3],
        borderTop: tokens.border.thin,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <h2
        id="saved-views-heading"
        style={{
          margin: `0 0 ${tokens.space[2]}px 0`,
          fontSize: tokens.font.size.md,
          lineHeight: tokens.font.lineHeight.tight,
          fontWeight: tokens.font.weight.semibold,
        }}
      >
        Saved Views
      </h2>

      <div style={{ display: 'flex', gap: tokens.space[2], marginBottom: tokens.space[3] }}>
        <label style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
            Save current view
          </span>
          <input
            value={viewName}
            onChange={(event) => setViewName(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSaveView();
            }}
            placeholder="View name"
            style={{
              width: '100%',
              minHeight: tokens.density.touchMin,
              boxSizing: 'border-box',
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background: tokens.color.surface[0],
              color: tokens.color.ink.primary,
              paddingInline: tokens.space[2],
            }}
          />
        </label>
        <button
          type="button"
          onClick={handleSaveView}
          disabled={!viewName.trim()}
          aria-label="Save current view"
          style={smallButtonStyle(!viewName.trim())}
        >
          <Icon name="bookmark" size={20} decorative />
          Save
        </button>
      </div>

      {savedViews.length === 0 ? (
        <p style={{ margin: 0, color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
          No saved views yet. Save your current view to bookmark it.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: tokens.space[2] }}>
          {savedViews.map((view) => (
            <li
              key={view.id}
              style={{
                padding: tokens.space[2],
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
                background: tokens.color.surface[1],
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: tokens.space[2] }}>
                <button
                  type="button"
                  onClick={() => handleApplyView(view)}
                  aria-label={`Apply saved view: ${view.name}`}
                  style={{
                    background: 'transparent',
                    border: 0,
                    color: tokens.color.ink.primary,
                    textAlign: 'left',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  <strong style={{ display: 'block', fontSize: tokens.font.size.sm }}>{view.name}</strong>
                  <span style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.xs }}>
                    Saved {new Date(view.createdAt).toLocaleDateString()}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(view)}
                  aria-label={`Delete saved view: ${view.name}`}
                  style={iconButtonStyle}
                >
                  <Icon name="delete" size={20} decorative />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {notice ? (
        <p role="status" style={{ margin: `${tokens.space[2]}px 0 0`, color: tokens.color.semantic.success }}>
          {notice}
        </p>
      ) : null}
    </section>
  );
}

function smallButtonStyle(disabled: boolean) {
  return {
    alignSelf: 'end',
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: disabled ? tokens.color.surface[2] : tokens.color.accent.primary,
    color: disabled ? tokens.color.ink.secondary : tokens.color.accent.on,
    paddingInline: tokens.space[2],
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const iconButtonStyle = {
  minHeight: tokens.density.touchMin,
  minWidth: tokens.density.touchMin,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: tokens.color.surface[2],
  color: tokens.color.ink.primary,
  cursor: 'pointer',
};
